import {
  Archive,
  CheckCheck,
  CircleDashed,
  DraftingCompass,
  Eye,
  Wrench,
  type LucideIcon,
} from 'lucide-react'

import { ProjectStatus } from '@brickup/shared/enums'
import { PROJECT_STATUS_LABELS } from '@brickup/shared/locales/de/enum-labels'
import { TONE_INK, type SelectOption, type Tone } from '@/components/base'

/**
 * How each status is drawn — the icon and the tone the menu, the filter and
 * the badge all share. One tone per status, so the glyph in the picker and the
 * pill beside it cannot be two colours.
 *
 * Key order is the lifecycle order every picker offers: draft → planning →
 * construction → done → archived. The public listing state is orthogonal to
 * that progression and sits ahead of the archive, the only terminal state.
 */
export const PROJECT_STATUS_STYLE = {
  DRAFT: { icon: CircleDashed, tone: 'default' },
  IN_PLANNING: { icon: DraftingCompass, tone: 'info' },
  UNDER_CONSTRUCTION: { icon: Wrench, tone: 'info' },
  DONE: { icon: CheckCheck, tone: 'success' },
  PUBLIC: { icon: Eye, tone: 'warning' },
  ARCHIVED: { icon: Archive, tone: 'default' },
} as const satisfies Record<ProjectStatus, { icon: LucideIcon; tone: Tone }>

/** Every status in lifecycle order — what the style table's keys spell out,
 *  named so a caller reads the order rather than re-deriving it. */
export const PROJECT_STATUS_ORDER = Object.keys(
  PROJECT_STATUS_STYLE,
) as ProjectStatus[]

/** The status list every picker offers, in lifecycle order. */
export const PROJECT_STATUS_OPTIONS: SelectOption[] = PROJECT_STATUS_ORDER.map(
  status => ({
    value: status,
    label: PROJECT_STATUS_LABELS[status],
    icon: PROJECT_STATUS_STYLE[status].icon,
    className: TONE_INK[PROJECT_STATUS_STYLE[status].tone],
  }),
)

/**
 * The same list with the public listing shut, for a project someone is already
 * assigned to.
 *
 * The row stays and goes dead rather than disappearing: a status missing from
 * the list reads as one this app does not have.
 */
const PROJECT_STATUS_OPTIONS_UNLISTABLE: SelectOption[] =
  PROJECT_STATUS_OPTIONS.map(option =>
    option.value === ProjectStatus.PUBLIC
      ? { ...option, disabled: true }
      : option,
  )

/** Which of the two a picker offers. */
export function projectStatusOptions({
  publicAllowed,
}: {
  publicAllowed: boolean
}): SelectOption[] {
  return publicAllowed
    ? PROJECT_STATUS_OPTIONS
    : PROJECT_STATUS_OPTIONS_UNLISTABLE
}

/** The statuses an overview counts, in lifecycle order; an archived project
 *  is nobody's work. */
export const COUNTED_PROJECT_STATUSES = PROJECT_STATUS_ORDER.filter(
  status => status !== ProjectStatus.ARCHIVED,
)
